/**
 * MessageMarkdownMemoized
 *
 * Shared Streamdown wrapper for message and task markdown.
 *
 * Primary responsibilities:
 * - Render completed markdown without streaming-only parser behavior
 * - Preserve explicit streaming props from active assistant messages
 *
 * Dependencies:
 * - streamdown
 */

import { FC, memo, useMemo } from 'react'
import { Streamdown, type Components, type StreamdownProps } from 'streamdown'
import { ImageWithPreview } from '@/components/image/image-with-preview'
import { getSafeRenderableImageUrl } from '@/lib/media/safe-url'
import { MessageMarkdownErrorBoundary } from './message-markdown-error-boundary'
import {
  StreamdownAnimateOnceBlock,
  streamdownStaticProps
} from './streamdown-animation'

const imagePreviewComponents = {
  img({ src, alt }) {
    const imageSrc = getSafeRenderableImageUrl(src)

    if (!imageSrc) {
      return null
    }

    return (
      <ImageWithPreview
        src={imageSrc}
        alt={typeof alt === 'string' ? alt : 'image'}
        className="animate-blur my-0 h-auto max-w-full cursor-pointer rounded-lg object-contain"
      />
    )
  }
} satisfies Components

const MessageMarkdownMemoizedComponent: FC<StreamdownProps> = ({
  BlockComponent = StreamdownAnimateOnceBlock,
  children,
  components,
  mode = streamdownStaticProps.mode,
  parseIncompleteMarkdown = streamdownStaticProps.parseIncompleteMarkdown,
  isAnimating = streamdownStaticProps.isAnimating,
  ...props
}) => {
  const componentsWithImagePreview = useMemo<Components>(
    () =>
      components
        ? {
            ...imagePreviewComponents,
            ...components
          }
        : imagePreviewComponents,
    [components]
  )

  const fallbackContent = typeof children === 'string' ? children : undefined

  return (
    <MessageMarkdownErrorBoundary fallbackContent={fallbackContent}>
      <Streamdown
        BlockComponent={BlockComponent}
        mode={mode}
        parseIncompleteMarkdown={parseIncompleteMarkdown}
        isAnimating={isAnimating}
        components={componentsWithImagePreview}
        {...props}
      >
        {children}
      </Streamdown>
    </MessageMarkdownErrorBoundary>
  )
}

export const MessageMarkdownMemoized = memo(MessageMarkdownMemoizedComponent)
MessageMarkdownMemoized.displayName = 'MessageMarkdownMemoized'
